import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Truck, Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { motion } from "framer-motion";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && user.role === "admin") {
    navigate(from, { replace: true });
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(from, { replace: true });
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2" data-testid="login-page">
      <div className="relative bg-[#0A0A0A] text-white p-10 md:p-16 flex flex-col justify-between overflow-hidden grain">
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="login-logo">
            <div className="w-8 h-8 bg-white flex items-center justify-center">
              <Truck size={18} className="text-[#FF4500]" strokeWidth={2}/>
            </div>
            <span className="font-display font-bold text-lg tracking-tight">ADTRAX</span>
          </Link>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="relative">
          <p className="font-mono-label text-xs text-[#FF4500]">DISPATCH CONTROL ROOM</p>
          <h1 className="font-display font-bold text-5xl md:text-6xl mt-4 leading-[1]">
            Sign in to<br/>keep the wheels<br/><span className="italic">turning.</span>
          </h1>
          <p className="mt-6 text-white/60 max-w-md">
            Admin-only access. Manage carrier signups, active loads, and billing status.
          </p>
        </motion.div>
        <p className="relative font-mono-label text-[10px] text-white/40">© {new Date().getFullYear()} ADTRAX LOGISTICS · EULESS, TX</p>
      </div>

      <div className="flex items-center justify-center p-10 md:p-16 bg-white">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md" data-testid="login-form">
          <Link to="/" className="inline-flex items-center gap-2 font-mono-label text-xs text-black/50 hover:text-[#FF4500]" data-testid="login-back-link">
            <ArrowLeft size={12}/> BACK TO SITE
          </Link>
          <h2 className="font-display font-bold text-4xl mt-6">Admin login.</h2>
          <p className="text-[#495057] mt-2">Enter your credentials to access the dispatch console.</p>

          <div className="mt-8 space-y-5">
            <div>
              <label className="font-mono-label text-[10px] text-black/50">EMAIL</label>
              <input
                required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full border border-black/15 px-4 py-3 text-sm focus:outline-none focus:border-[#FF4500]"
                data-testid="login-input-email" autoComplete="username"
              />
            </div>
            <div>
              <label className="font-mono-label text-[10px] text-black/50">PASSWORD</label>
              <input
                required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full border border-black/15 px-4 py-3 text-sm focus:outline-none focus:border-[#FF4500]"
                data-testid="login-input-password" autoComplete="current-password"
              />
            </div>
            {error && (
              <div className="border border-[#FF4500] bg-[#FFF5F0] p-3 text-sm text-[#B23A00]" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit" disabled={loading}
              className="sharp-btn w-full bg-black text-white py-4 font-medium text-sm hover:bg-[#FF4500] inline-flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {loading ? <><Loader2 size={16} className="animate-spin"/> Signing in…</> : <>Sign In <ArrowRight size={16}/></>}
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
